import { useState } from 'react'
import { format, subDays } from 'date-fns'

import { Popup } from '@/ui/Popup'
import { DatePicker } from '@/ui/DatePicker'
import { ShowingOption } from '../../config/showingOptions'
import ArrowDownIcon from '@/modules/CombinedLogs/assets/arrow-down.svg'
import styles from './ShowingDropdown.module.scss'

export type DateRange = { from: Date | null; to: Date | null }

interface MenuProps {
    newestFirst: boolean
    onToggleSort: () => void
    options: ShowingOption[]
    selectedOption: ShowingOption
    onSelectOption: (option: ShowingOption) => void
    dateRange: DateRange
    onDateRangeChange: (range: DateRange) => void
}

const MIN_DATE = subDays(new Date(), 28)

const ShowingMenu = ({
    newestFirst,
    onToggleSort,
    options,
    selectedOption,
    onSelectOption,
    dateRange,
    onDateRangeChange,
}: MenuProps) => {
    const [activeCalendar, setActiveCalendar] = useState<'from' | 'to' | null>(null)

    const handleFromChange = (date: Date) => {
        const newRange = { from: date, to: dateRange.to && date > dateRange.to ? null : dateRange.to }
        onDateRangeChange(newRange)
        setActiveCalendar(newRange.to ? null : 'to')
    }

    const handleToChange = (date: Date) => {
        onDateRangeChange({ ...dateRange, to: date })
        setActiveCalendar(null)
    }

    const handleClear = () => {
        onDateRangeChange({ from: null, to: null })
        setActiveCalendar(null)
    }

    const toggleCalendar = (cal: 'from' | 'to') => setActiveCalendar((prev) => (prev === cal ? null : cal))

    return (
        <div className={styles.menu}>
            <button className={styles.sortItem} onClick={onToggleSort}>
                Sort by - {newestFirst ? 'newest first' : 'oldest first'}
            </button>

            <div className={styles.divider} />

            <div className={styles.showingSection}>
                <span className={styles.showingLabel}>Showing</span>
                {options.map((option) => (
                    <button
                        key={option.label}
                        className={styles.optionItem}
                        data-selected={option.label === selectedOption.label}
                        onClick={() => onSelectOption(option)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            <div className={styles.divider} />

            <div className={styles.selectTimeSection}>
                <div className={styles.selectTimeHeader}>
                    <span className={styles.selectTimeTitle}>Select time</span>
                    {dateRange.from || dateRange.to ? (
                        <button className={styles.clearButton} onClick={handleClear}>
                            Clear
                        </button>
                    ) : (
                        <span className={styles.clearLabel}>Clear</span>
                    )}
                </div>
                <div className={styles.dateInputRow}>
                    <div className={styles.dateInputGroup}>
                        <span className={styles.dateLabel}>From</span>
                        <button
                            className={styles.dateInput}
                            data-active={activeCalendar === 'from'}
                            data-filled={!!dateRange.from}
                            onClick={() => toggleCalendar('from')}
                        >
                            {dateRange.from ? format(dateRange.from, 'dd/MM/yy') : 'From'}
                        </button>
                    </div>
                    <div className={styles.dateInputGroup}>
                        <span className={styles.dateLabel}>To</span>
                        <button
                            className={styles.dateInput}
                            data-active={activeCalendar === 'to'}
                            data-filled={!!dateRange.to}
                            onClick={() => toggleCalendar('to')}
                        >
                            {dateRange.to ? format(dateRange.to, 'dd/MM/yy') : 'To'}
                        </button>
                    </div>
                </div>

                {activeCalendar === 'from' && (
                    <DatePicker
                        value={dateRange.from}
                        onChange={handleFromChange}
                        disableDateBefore={MIN_DATE}
                        disableDateFrom={dateRange.to ?? undefined}
                        className={styles.inlineCalendar}
                    />
                )}
                {activeCalendar === 'to' && (
                    <DatePicker
                        value={dateRange.to}
                        onChange={handleToChange}
                        disableDateBefore={dateRange.from ?? MIN_DATE}
                        className={styles.inlineCalendar}
                    />
                )}
            </div>
        </div>
    )
}

interface Props extends MenuProps {}

export const ShowingDropdown = (props: Props) => {
    const { dateRange } = props
    const rangeLabel =
        dateRange.from && dateRange.to ? `${format(dateRange.from, 'dd/MM')} - ${format(dateRange.to, 'dd/MM')}` : null

    return (
        <Popup
            placement="bottom-end"
            offset={4}
            triggerComponent={(isOpen) => (
                <button className={styles.trigger}>
                    Showing <strong>{rangeLabel ?? props.selectedOption.label}</strong>
                    <span className={isOpen ? styles.arrowOpen : styles.arrow}>
                        <ArrowDownIcon />
                    </span>
                </button>
            )}
        >
            <ShowingMenu {...props} />
        </Popup>
    )
}
